import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Trash2, Plus, Upload, Check } from "lucide-react";
import PlatformIcon from "./PlatformIcon";

const MAX_LINKS = 15;
const MAX_PROMOS = 5;
const PROMO_ACCENTS = [
  { v: "magenta", color: "#F71868" },
  { v: "gold", color: "#D4A574" },
  { v: "cognac", color: "#D97757" },
  { v: "sage", color: "#7FA88E" },
  { v: "eucalyptus", color: "#5FA59E" },
  { v: "neon", color: "#FF1A8C" },
  { v: "aqua", color: "#0066FF" },
];
const PLATFORM_SUGGESTIONS = [
  "OnlyFans", "Fansly", "Loverfans", "Fanvue", "JustForFans", "IsMyGirl",
  "Patreon", "Buy Me a Coffee", "Ko-Fi",
  "Instagram", "X", "TikTok", "Snapchat", "Telegram", "Bluesky",
  "Linktree", "Beacons", "Egen nettside",
];

export default function CreatorEditModal({ open, onClose, initial, onSaved }) {
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState(initial?.creator_theme || "noir");
  const [tagline, setTagline] = useState(initial?.creator_tagline || "");
  const [about, setAbout] = useState(initial?.creator_about || "");
  const [links, setLinks] = useState(initial?.creator_links || []);
  const [promos, setPromos] = useState([]);
  const [bannerPath, setBannerPath] = useState(initial?.creator_banner_url || null);
  const [creatorAvatarUrl, setCreatorAvatarUrl] = useState(initial?.creator_avatar_url || null);
  const [handle, setHandle] = useState(initial?.creator_handle || "");
  const [handleSaving, setHandleSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [allowDm, setAllowDm] = useState(!!initial?.allow_creator_dm);
  const [linkVisible, setLinkVisible] = useState(initial?.creator_link_visible !== false);
  const [showLinkOnProfile, setShowLinkOnProfile] = useState(initial?.show_creator_link_on_profile === true);
  const [linkText, setLinkText] = useState(initial?.creator_link_text || "");
  const [contactPlatform, setContactPlatform] = useState(initial?.contact_platform || "");
  const [contactHandle, setContactHandle] = useState(initial?.contact_handle || "");
  const [seenSeparationInfo, setSeenSeparationInfo] = useState(!!initial?.seen_separation_info);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/creator-themes").then((r) => setThemes(r.data.themes || []));
      setTheme(initial?.creator_theme || "noir");
      setTagline(initial?.creator_tagline || "");
      setAbout(initial?.creator_about || "");
      setLinks(initial?.creator_links || []);
      setBannerPath(initial?.creator_banner_url || null);
      setCreatorAvatarUrl(initial?.creator_avatar_url || null);
      setHandle(initial?.creator_handle || "");
      setAllowDm(!!initial?.allow_creator_dm);
      setLinkVisible(initial?.creator_link_visible !== false);
      setShowLinkOnProfile(initial?.show_creator_link_on_profile === true);
      setLinkText(initial?.creator_link_text || "");
      setContactPlatform(initial?.contact_platform || "");
      setContactHandle(initial?.contact_handle || "");
      setSeenSeparationInfo(!!initial?.seen_separation_info);
      if (initial?.username) {
        api.get(`/users/${initial.username}/creator-promos`)
          .then((r) => setPromos(r.data?.results || []))
          .catch(() => setPromos([]));
      } else {
        setPromos([]);
      }
    }
  }, [open, initial]);

  if (!open) return null;

  const addLink = () => {
    if (links.length >= MAX_LINKS) { toast.error(`Maks ${MAX_LINKS} lenker/QR-koder`); return; }
    setLinks([...links, { platform: "", label: "", url: "", type: "url" }]);
  };
  const addQrLink = () => {
    if (links.length >= MAX_LINKS) { toast.error(`Maks ${MAX_LINKS} lenker/QR-koder`); return; }
    setLinks([...links, { platform: "", label: "", type: "qr", qr_image_url: "" }]);
  };
  const updateLink = (i, patch) => setLinks(links.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));

  const uploadQr = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/me/creator-link-qr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateLink(i, { qr_image_url: r.data.path });
      toast.success("QR-bilde lagret");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke laste opp QR");
    }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/me/creator-banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBannerPath(r.data.path);
      toast.success("Banner lastet opp");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke laste opp");
    }
  };

  const removeBanner = async () => {
    await api.delete("/me/creator-banner");
    setBannerPath(null);
  };

  const uploadCreatorAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setAvatarUploading(true);
    try {
      const r = await api.post("/me/creator-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCreatorAvatarUrl(r.data.creator_avatar_url);
      toast.success("Skaper-avatar lastet opp");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Kunne ikke laste opp");
    } finally {
      setAvatarUploading(false);
    }
  };

  const removeCreatorAvatar = async () => {
    try {
      await api.delete("/me/creator-avatar");
      setCreatorAvatarUrl(null);
      toast.success("Skaper-avatar fjernet");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Kunne ikke fjerne");
    }
  };

  const saveHandle = async () => {
    const trimmed = (handle || "").trim();
    if (trimmed.length < 3) { toast.error("Artistnavn må være minst 3 tegn"); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) { toast.error("Kun bokstaver, tall og _ tillatt"); return; }
    setHandleSaving(true);
    try {
      await api.put("/users/me/creator-handle", { creator_handle: trimmed });
      toast.success(`Artistnavn satt til @${trimmed}`);
    } catch (er) {
      toast.error(er.response?.data?.detail || "Kunne ikke lagre artistnavn");
    } finally {
      setHandleSaving(false);
    }
  };

  const removeHandle = async () => {
    if (!window.confirm("Fjerne artistnavnet? Skapersiden faller tilbake til ditt private brukernavn.")) return;
    try {
      await api.delete("/users/me/creator-handle");
      setHandle("");
      toast.success("Artistnavn fjernet");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Kunne ikke fjerne");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Lenker/promoer/kontakt redigeres nå KUN i Skaper Studio – derfor ikke sendt herfra.
      await api.patch("/me/creator-page", {
        creator_theme: theme,
        creator_tagline: tagline,
        creator_about: about,
        allow_creator_dm: allowDm,
      });
      // Profil-separasjon: lagre koblings-synlighet
      await api.patch("/users/me/creator-link-visible", { visible: linkVisible });
      // Lenke fra privatprofil til skaperprofil (banner på privat side)
      await api.patch("/users/me/profile", {
        show_creator_link_on_profile: showLinkOnProfile,
        creator_link_text: linkText.trim(),
      });
      toast.success("Lagret");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke lagre");
    } finally {
      setSaving(false);
    }
  };

  const ackSeparationInfo = async () => {
    setSeenSeparationInfo(true);
    try { await api.post("/me/seen-separation-info"); } catch { /* ignorer */ }
  };

  const addPromo = () => {
    if (promos.length >= MAX_PROMOS) { toast.error(`Maks ${MAX_PROMOS} promoer`); return; }
    setPromos([...promos, { title: "", body: "", link: "", link_label: "", accent: "magenta" }]);
  };
  const updatePromo = (i, patch) => setPromos(promos.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const removePromo = (i) => setPromos(promos.filter((_, idx) => idx !== i));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto pt-24 pb-10 px-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[var(--noir-elev)] border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">Rediger skaperside</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-[var(--cream-soft)]" /></button>
        </div>

        <div className="p-6 space-y-8">
          {/* SEPARASJONSINFO (Iter 47) – tvunget visning første gang */}
          {!seenSeparationInfo && (
            <section className="border-2 border-[var(--magenta)] bg-[var(--magenta)]/10 p-5" data-testid="separation-info-block">
              <h3 className="font-fraunces italic text-xl text-[var(--cream)] mb-3">Viktig om skaperprofilen din</h3>
              <ul className="space-y-2 text-sm text-[var(--cream)] mb-4 list-disc list-inside leading-relaxed">
                <li>Din <span className="text-[var(--magenta)] font-meta">vanlige profil</span> og din <span className="text-[var(--magenta)] font-meta">skaperprofil</span> er <strong>to separate flater</strong>.</li>
                <li>Bilder, video og album er knyttet til den profilen de ble lastet opp fra. Innhold på vanlig profil vises <strong>ikke</strong> automatisk på skapersiden, og omvendt.</li>
                <li>Du har egen skaper-avatar, eget artistnavn og egen skaper-innboks.</li>
                <li>Andre brukere ser som standard at de to profilene er koblet. Du kan slå av koblingen under for å holde dem helt adskilt – da kan ingen se at de to profilene tilhører samme person.</li>
              </ul>
              <button
                onClick={ackSeparationInfo}
                className="px-5 py-2.5 bg-[var(--magenta)] text-white font-meta tracking-[0.15em] text-xs hover:opacity-90"
                data-testid="separation-info-ack-btn"
              >
                JEG HAR LEST DETTE
              </button>
            </section>
          )}

          {/* TEMA */}
          <section>
            <h3 className="font-meta text-[var(--cream-soft)] mb-3">VELG TEMA</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="theme-picker">
              {themes.map((th) => {
                const sel = th.id === theme;
                return (
                  <button
                    key={th.id}
                    onClick={() => setTheme(th.id)}
                    data-testid={`theme-${th.id}`}
                    className={`relative text-left p-3 transition outline-none ${
                      sel
                        ? "ring-2 ring-[var(--magenta)] ring-offset-2 ring-offset-[var(--noir-elev)] border border-[var(--magenta)]"
                        : "border border-white/10 hover:border-white/30"
                    }`}
                    style={{ background: th.bg, color: th.text }}
                  >
                    {sel && (
                      <span
                        data-testid={`theme-active-${th.id}`}
                        className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--magenta)] text-[var(--noir)] shadow-md"
                        aria-label="Valgt tema"
                      >
                        <Check className="w-3 h-3 stroke-[3]" />
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-4 h-4 rounded-full" style={{ background: th.accent }} />
                      <div className="font-medium" style={{ fontFamily: `'${th.heading_font}', serif`, fontStyle: th.heading_style }}>
                        {th.name}
                      </div>
                    </div>
                    <div className="text-xs opacity-70">{th.blurb}</div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* BANNER */}
          <section>
            <h3 className="font-meta text-[var(--cream-soft)] mb-3">BANNER (VALGFRITT)</h3>
            <div className="flex gap-3 items-center">
              <label className="px-4 py-2 border border-white/20 text-sm text-[var(--cream)] cursor-pointer hover:bg-white/5 flex items-center gap-2">
                <Upload className="h-4 w-4" /> Last opp bilde
                <input type="file" accept="image/*" className="hidden" onChange={uploadBanner} data-testid="banner-upload" />
              </label>
              {bannerPath && (
                <button onClick={removeBanner} className="text-sm text-[var(--magenta)] hover:underline">Fjern banner</button>
              )}
              <span className="text-xs text-[var(--cream-faint)]">Anbefalt: 1600×600 (16:6). Maks 8 MB.</span>
            </div>
            {bannerPath && (
              <img
                src={bannerPath.startsWith("http") ? bannerPath : `${process.env.REACT_APP_BACKEND_URL}/api/creator-banners/raw?path=${encodeURIComponent(bannerPath)}`}
                alt="" className="mt-3 w-full h-32 object-cover border border-white/10"
              />
            )}
          </section>

          {/* TAGLINE */}
          <section>
            <h3 className="font-meta text-[var(--cream-soft)] mb-2">TAGLINE</h3>
            <input
              data-testid="creator-tagline"
              value={tagline} onChange={(e) => setTagline(e.target.value)}
              maxLength={80}
              placeholder="Forfatter og creator fra Oslo"
              className="w-full bg-[var(--noir)] border border-white/10 px-4 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/50 outline-none"
            />
            <div className="text-xs text-[var(--cream-faint)] text-right mt-1">{tagline.length}/80</div>
          </section>

          {/* OM */}
          <section>
            <h3 className="font-meta text-[var(--cream-soft)] mb-2">OM MEG (LANGFORM)</h3>
            <textarea
              data-testid="creator-about"
              value={about} onChange={(e) => setAbout(e.target.value)}
              rows={4} maxLength={1200}
              placeholder="En lengre presentasjon som vises nederst på skapersiden din…"
              className="w-full bg-[var(--noir)] border border-white/10 px-4 py-2 text-[var(--cream)] focus:border-[var(--magenta)]/50 outline-none"
            />
            <div className="text-xs text-[var(--cream-faint)] text-right mt-1">{about.length}/1200</div>
          </section>

          {/* SKAPER-IDENTITET (artistnavn + avatar) – SEPARAT FRA PRIVAT PROFIL */}
          <section data-testid="creator-identity-section" className="border border-[var(--magenta)]/30 p-4 bg-[var(--magenta)]/5">
            <h3 className="font-meta text-[var(--magenta)] mb-1 tracking-[0.2em] text-[10px]">SKAPER-IDENTITET</h3>
            <p className="text-xs text-[var(--cream-soft)] mb-4 leading-relaxed">
              Skaper-profilen er en separat identitet med eget navn og bilde. Privat profil forblir uberørt.
            </p>

            <div className="flex items-start gap-4 mb-5">
              <div className="w-20 h-20 shrink-0 border-2 border-[var(--magenta)] overflow-hidden bg-[var(--noir-2)]">
                {creatorAvatarUrl ? (
                  <img src={creatorAvatarUrl} alt="" className="w-full h-full object-cover" data-testid="creator-avatar-preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--cream-faint)] text-2xl font-fraunces italic">
                    {(handle || initial?.username || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="font-meta text-[10px] text-[var(--cream-soft)] mb-2">SKAPER-AVATAR</div>
                <div className="flex flex-wrap gap-2 items-center">
                  <label className={`px-3 py-1.5 border border-white/20 text-xs text-[var(--cream)] cursor-pointer hover:bg-white/5 flex items-center gap-2 ${avatarUploading ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload className="h-3.5 w-3.5" /> {avatarUploading ? "Laster opp..." : (creatorAvatarUrl ? "Bytt" : "Last opp")}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadCreatorAvatar} data-testid="creator-avatar-upload" />
                  </label>
                  {creatorAvatarUrl && (
                    <button onClick={removeCreatorAvatar} className="text-xs text-[var(--magenta)] hover:underline" data-testid="creator-avatar-remove">
                      Fjern
                    </button>
                  )}
                  <span className="text-[10px] text-[var(--cream-faint)]">Maks 5 MB. Ingen eksplisitt innhold.</span>
                </div>
              </div>
            </div>

            <div>
              <div className="font-meta text-[10px] text-[var(--cream-soft)] mb-2">ARTISTNAVN (BRUKES PÅ SKAPER-URL OG SOM SYNLIG NAVN)</div>
              <div className="flex gap-2 items-center max-w-md">
                <span className="font-meta text-[var(--cream-faint)]">@</span>
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="f.eks. RoseNoire"
                  maxLength={24}
                  className="flex-1 bg-[var(--noir-2)] border border-white/10 px-3 py-1.5 text-sm text-[var(--cream)] focus:border-[var(--magenta)] outline-none"
                  data-testid="creator-handle-input-modal"
                />
                <button onClick={saveHandle} disabled={handleSaving || !handle} className="px-3 py-1.5 bg-[var(--magenta)] text-white text-xs font-meta tracking-[0.15em] hover:opacity-90 disabled:opacity-40" data-testid="creator-handle-save-modal">
                  {handleSaving ? "..." : "LAGRE"}
                </button>
                {initial?.creator_handle && (
                  <button onClick={removeHandle} className="text-xs text-[var(--cream-soft)] hover:text-[var(--magenta)]" data-testid="creator-handle-remove-modal">
                    Fjern
                  </button>
                )}
              </div>
              {initial?.creator_handle && (
                <div className="text-[10px] text-[var(--cream-faint)] mt-2">
                  URL: <span className="text-[var(--magenta)]">/skaper/{initial.creator_handle}</span>
                </div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-white/10">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="creator-allow-dm-toggle">
                <input
                  type="checkbox"
                  checked={allowDm}
                  onChange={(e) => setAllowDm(e.target.checked)}
                  className="mt-1 accent-[var(--magenta)] w-4 h-4"
                />
                <div>
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream)]">
                    TILLAT MELDINGER VIA SKAPERSIDEN
                  </div>
                  <div className="text-xs text-[var(--cream-soft)] mt-1 leading-relaxed">
                    Hvis avskrudd (anbefalt) vises ikke «Send melding»-knappen på skapersiden din.
                    Fans må ta kontakt på de eksterne plattformene du har lenket til – mindre støy i innboksen.
                  </div>
                </div>
              </label>
            </div>
          </section>

          {/* PROFIL-KOBLING (synlighet + lenkebanner) */}
          <section className="border border-white/10 p-4 bg-[var(--noir-2)]" data-testid="creator-link-toggle-section">
            <h3 className="font-meta tracking-[0.2em] text-[10px] text-[var(--cream-soft)] mb-1">PROFIL-KOBLING</h3>
            <p className="text-xs text-[var(--cream-soft)] mb-3 leading-relaxed">
              {linkVisible
                ? "Andre kan se at din vanlige profil og skaperprofilen tilhører samme person."
                : "Koblingen er skjult. Andre ser de to profilene som separate personer. Eier og staff ser alltid koblingen."}
            </p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={linkVisible}
                onChange={(e) => setLinkVisible(e.target.checked)}
                className="h-4 w-4 accent-[var(--magenta)]"
                data-testid="creator-link-visible-toggle"
              />
              <span className="text-sm text-[var(--cream)]">Vis kobling mellom mine to profiler</span>
            </label>

            <div className="mt-4 pt-4 border-t border-white/10">
              <label className="flex items-center gap-3 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={showLinkOnProfile}
                  onChange={(e) => setShowLinkOnProfile(e.target.checked)}
                  className="h-4 w-4 accent-[var(--magenta)]"
                  data-testid="show-creator-link-on-profile-toggle"
                />
                <span className="text-sm text-[var(--cream)]">Vis banner med lenke til skapersiden på min privatprofil</span>
              </label>
              <p className="text-xs text-[var(--cream-faint)] mb-3 ml-7">
                Synlig banner under «Fremhevede bilder» på privatprofilen. Klikkes på fører til skapersiden din.
              </p>
              {showLinkOnProfile && (
                <div className="ml-7">
                  <label className="block font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-1.5">
                    EGEN TEKST PÅ BANNER (valgfri)
                  </label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value.slice(0, 60))}
                    maxLength={60}
                    placeholder="Besøk min skaperside"
                    className="input-editorial w-full"
                    data-testid="creator-link-text-input"
                  />
                  <p className="text-xs text-[var(--cream-faint)] mt-1">
                    {linkText.length}/60 tegn. Tomt = standardtekst «Besøk min skaperside».
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2 text-sm text-[var(--cream-soft)] hover:text-[var(--cream)]">Avbryt</button>
          <button onClick={save} disabled={saving}
            data-testid="creator-save-btn"
            className="px-6 py-2 bg-[var(--magenta)] text-black font-meta disabled:opacity-50">
            {saving ? "LAGRER…" : "LAGRE"}
          </button>
        </div>
      </div>
    </div>
  );
}
